import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useStore } from "../../store";
import { useTab } from "../../store/useTab";
import { formatDate } from "../../utils/helpers";
import api from "../../service/api";
import { PasswordInput } from "../../components/password-input";
import { Button } from "../../components/Button";
import {
  User,
  Mail,
  FileText,
  Building2,
  Shield,
  Calendar,
  KeyRound,
} from "lucide-react";

function getUserTypeLabel(userType: string): string {
  const typeMap: { [key: string]: string } = {
    ADMIN: "Administrador",
    SUPERADMIN: "Super Administrador",
    USERENTERPRISE: "Usuário Empresarial",
    USER: "Usuário",
  };

  return typeMap[userType] || userType;
}

function getSectorLabel(sector: string): string {
  const sectorMap: { [key: string]: string } = {
    PUBLIC: "Público",
    PRIVATE: "Privado",
  };

  return sectorMap[sector] || sector || "Não informado";
}

export function Profile() {
  const user = useStore((store) => store.user);
  const setUser = useStore((store) => store.setUser);
  const setCurrentTab = useTab((store) => store.setTab);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPass, setSavingPass] = useState(false);

  useEffect(() => {
    setCurrentTab("Perfil");
  }, [setCurrentTab]);

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user?.id) return;
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.info("Preencha a senha atual e a nova senha.");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("A confirmação não confere com a nova senha.");
      return;
    }

    setSavingPass(true);
    try {
      const { data } = await api.patch("/users/update-pass", {
        id: user.id,
        old_password: oldPassword,
        new_password: newPassword,
      });
      const updated = { ...user, ...data };
      setUser(updated);
      localStorage.setItem("@apcap:user", JSON.stringify(updated));
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Senha alterada com sucesso.");
    } catch {
      toast.error(
        "Não foi possível alterar a senha. Verifique se a senha atual está correta."
      );
    } finally {
      setSavingPass(false);
    }
  }

  return (
    <>
      <header className="mb-8 border-b border-zinc-200/80 pb-6 dark:border-zinc-800">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-white sm:text-3xl">
          Meu perfil
        </h1>
        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
          Visualize e gerencie suas informações pessoais e segurança da conta.
        </p>
      </header>

      <div className="space-y-6">
        {/* Card de Informações Pessoais */}
        <div className="ds-card overflow-hidden dark:shadow-none">
          <div className="border-b border-zinc-200/90 bg-zinc-50/80 px-6 py-4 dark:border-zinc-800 dark:bg-zinc-900/50">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
                <User className="h-5 w-5" />
                Informações Pessoais
              </h2>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-1">
                <label className="text-sm font-medium text-zinc-600 dark:text-zinc-400 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Nome Completo
                </label>
                <p className="text-base text-zinc-900 dark:text-white font-medium">
                  {user?.name || "Não informado"}
                </p>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-zinc-600 dark:text-zinc-400 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  E-mail
                </label>
                <p className="text-base text-zinc-900 dark:text-white font-medium">
                  {user?.email || "Não informado"}
                </p>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-zinc-600 dark:text-zinc-400 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  CPF/CNPJ
                </label>
                <p className="text-base text-zinc-900 dark:text-white font-medium">
                  {user?.document || "Não informado"}
                </p>
              </div>

              {user?.CNPJ && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-zinc-600 dark:text-zinc-400 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    CNPJ
                  </label>
                  <p className="text-base text-zinc-900 dark:text-white font-medium">
                    {user.CNPJ}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Alterar senha */}
        <div className="ds-card overflow-hidden dark:shadow-none">
          <div className="border-b border-zinc-200/90 bg-zinc-50/80 px-6 py-4 dark:border-zinc-800 dark:bg-zinc-900/50">
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Alterar senha
            </h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              Para sua segurança, informe a senha atual e escolha uma nova senha.
            </p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="p-6 space-y-4 max-w-lg">
            <div>
              <label
                htmlFor="profile-old-password"
                className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300"
              >
                Senha atual
              </label>
              <PasswordInput
                id="profile-old-password"
                autoComplete="current-password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
              />
            </div>
            <div>
              <label
                htmlFor="profile-new-password"
                className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300"
              >
                Nova senha
              </label>
              <PasswordInput
                id="profile-new-password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            <div>
              <label
                htmlFor="profile-confirm-password"
                className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300"
              >
                Confirmar nova senha
              </label>
              <PasswordInput
                id="profile-confirm-password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repita a nova senha"
              />
            </div>
            <Button
              type="submit"
              className="h-10"
              disabled={savingPass}
            >
              {savingPass ? "Salvando…" : "Atualizar senha"}
            </Button>
          </form>
        </div>

        {/* Card de Informações da Conta */}
        <div className="ds-card overflow-hidden dark:shadow-none">
          <div className="border-b border-zinc-200/90 bg-zinc-50/80 px-6 py-4 dark:border-zinc-800 dark:bg-zinc-900/50">
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Informações da Conta
            </h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-1">
                <label className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                  Tipo de Usuário
                </label>
                <div className="mt-1">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300">
                    {getUserTypeLabel(user?.user_type || "")}
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                  Setor
                </label>
                <div className="mt-1">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                    {getSectorLabel(user?.sector || "")}
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-zinc-600 dark:text-zinc-400 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Data de Criação
                </label>
                <p className="text-base text-zinc-900 dark:text-white font-medium">
                  {user?.created_at
                    ? formatDate(user.created_at, true)
                    : "Não informado"}
                </p>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-zinc-600 dark:text-zinc-400 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Última Atualização
                </label>
                <p className="text-base text-zinc-900 dark:text-white font-medium">
                  {user?.updated_at
                    ? formatDate(user.updated_at, true)
                    : "Não informado"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Card de ID */}
        <div className="ds-card overflow-hidden dark:shadow-none">
          <div className="border-b border-zinc-200/90 bg-zinc-50/80 px-6 py-4 dark:border-zinc-800 dark:bg-zinc-900/50">
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
              Identificação
            </h2>
          </div>

          <div className="p-6">
            <div className="space-y-1">
              <label className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                ID do Usuário
              </label>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 font-mono bg-zinc-100 dark:bg-zinc-900/50 px-3 py-2 rounded border border-zinc-200 dark:border-zinc-700">
                {user?.id || "Não disponível"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

